import React, {
  createContext,
  useCallback,
  useContext,
  useReducer,
} from "react";
import useSound from "use-sound";

import African3 from "../sounds/African3.mp3";
import Camera from "../sounds/Camera.mp3";

interface IAudioState {
  place: boolean;
  globalPlace: boolean;
  disconnected: boolean;
  reconnected: boolean;
  pixelAvailable: boolean;
  pixelBucketFull: boolean;
  pixelUndo: boolean;
  uiClick: boolean;
  cameraShutter: boolean;
}

const Defaults: IAudioState = {
  place: false,
  globalPlace: false,
  disconnected: false,
  reconnected: false,
  pixelAvailable: false,
  pixelBucketFull: false,
  pixelUndo: false,
  uiClick: false,
  cameraShutter: false,
};

type AudioActions =
  | ["enable", keyof IAudioState]
  | ["disable", keyof IAudioState];

interface IPlay {
  (type: "UI_CLICK" | "CAMERA_SHUTTER"): unknown;
}

const context = createContext<{
  state: IAudioState;
  dispatch: React.ActionDispatch<[AudioActions]>;
  play: IPlay;
}>({} as any);

export const useAudio = () => useContext(context);

const AudioContext = ({ children }: React.PropsWithChildren) => {
  const [state, dispatch] = useReducer<IAudioState, [AudioActions]>(
    (state, [enable, key]) => {
      return {
        ...state,
        [key]: enable === "enable",
      };
    },
    Defaults,
  );

  const [African3Sound] = useSound(African3, { volume: 0.5 });
  const [CameraSound] = useSound(Camera, { volume: 0.25 });

  const play: IPlay = useCallback(
    (type) => {
      if (type === "UI_CLICK" && state.uiClick) African3Sound();
      if (type === "CAMERA_SHUTTER" && state.cameraShutter) CameraSound();
    },
    [African3Sound, CameraSound, state],
  );

  return (
    <context.Provider value={{ state, dispatch, play }}>
      {children}
    </context.Provider>
  );
};

export default React.memo(AudioContext);
